import React from 'react';
import { Zap, ArrowRight, Star } from 'lucide-react';

const UpgradeSuggestion = ({ usageData, limits, onUpgrade }) => {
  // Analyze usage patterns to provide personalized suggestions
  const getPersonalizedMessage = () => {
    const exportUsage = usageData?.monthlyExports?.usageCount || 0;
    const exportLimit = limits?.monthly_exports_limit || 2;

    if (exportUsage >= exportLimit) {
      return {
        title: 'You\'ve reached your export limit',
        message: 'Upgrade to Premium for unlimited exports and never worry about limits again.',
        benefits: [
          'Unlimited symptom exports',
          'Export in multiple formats (PDF, CSV, Word)',
          'Professional doctor reports',
          'Priority support'
        ]
      };
    }

    if (exportUsage >= exportLimit * 0.7) {
      return {
        title: 'You\'re a power user!',
        message: 'You\'re using your features actively. Premium gives you unlimited access.',
        benefits: [
          'No monthly limits on any feature',
          'Advanced analytics and insights',
          'Caregiver management',
          'Extended data storage'
        ]
      };
    }

    return {
      title: 'Unlock the full potential',
      message: 'Upgrade to Premium and get access to all features without limitations.',
      benefits: [
        'Unlimited exports and analytics',
        'Connect with caregivers',
        '6 months of data storage',
        'Priority customer support'
      ]
    };
  };

  const suggestion = getPersonalizedMessage();

  return (
    <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-lg overflow-hidden">
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="bg-white bg-opacity-20 rounded-full p-3">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">{suggestion?.title}</h3>
              <p className="text-blue-100 mt-1">{suggestion?.message}</p>
            </div>
          </div>
          <Star className="w-8 h-8 text-yellow-300" />
        </div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {suggestion?.benefits?.map((benefit, index) => (
            <div key={index} className="flex items-center space-x-2 text-white">
              <div className="w-5 h-5 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold">✓</span>
              </div>
              <span className="text-sm">{benefit}</span>
            </div>
          ))}
        </div>

        {/* Pricing Highlight */}
        <div className="bg-white bg-opacity-10 rounded-lg p-4 mb-6">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="text-blue-100 text-sm mb-1">Premium Plan</p>
              <div className="flex items-baseline space-x-2">
                <span className="text-4xl font-bold text-white">£4.99</span>
                <span className="text-blue-100">/month</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-blue-100 text-sm mb-1">Annual Plan</p>
              <div className="flex items-baseline space-x-2">
                <span className="text-4xl font-bold text-white">£49.99</span>
                <span className="text-blue-100">/year</span>
              </div>
              <p className="text-xs text-yellow-300 font-medium mt-1">Save 17%</p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <button
          onClick={onUpgrade}
          className="w-full bg-white text-blue-600 py-4 px-6 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors flex items-center justify-center space-x-2 shadow-lg"
        >
          <span>Upgrade to Premium</span>
          <ArrowRight className="w-6 h-6" />
        </button>

        <p className="text-center text-blue-100 text-sm mt-4">
          Cancel anytime • No hidden fees • Instant activation
        </p>
      </div>
    </div>
  );
};

export default UpgradeSuggestion;